/* QUELL-604 — dealer-source files (SketchUp, Revit, DWG/CAD) rendered
   inside the PDP's "Renders, photography & the drawing set" accordion.

   Fetches metadata from the sales-app's public catalogue endpoint (kind /
   label / format — no download URLs), then injects one <details.res-group>
   per kind directly into the same accordion the metafield rows render into.
   Rows whose kind keyword (revit / sketchup / cad) already appears in a
   metafield category heading are skipped, so we do not double-print (e.g.
   metafield "Omni One Plus CAD" already covers DWG for that product).

   Downloads reuse the same authenticated ticket flow the dealer library
   at /pages/dealer-cad-revit-sketchup uses — no WorkDrive/Dropbox redirect,
   no Shopify Files copy of the source binary. This is what lets 100+ MB
   .skp files stream to a specifier through the site rather than blowing
   past Shopify Files' 20 MB API upload cap.

   Sales-app deploys via Dokploy (minutes) while the theme syncs in
   seconds, so a 404 on the summary endpoint hides gracefully — same rule
   assets/section-dealer-cad-library.js applies to the CAD library. */
(function () {
  "use strict";

  var slot = document.querySelector("[data-dealer-files-slot]");
  if (!slot) return;

  var handle = slot.getAttribute("data-product-handle") || "";
  var productName = slot.getAttribute("data-product-title") || "";
  var SIGN_IN_REQUIRED = slot.getAttribute("data-sign-in-required") || "Please sign in with an approved dealer account and try again.";
  var FINDING_LABEL = slot.getAttribute("data-finding-label") || "Finding…";
  function msg(name, fallback) {
    return slot.getAttribute("data-msg-" + name) || fallback;
  }
  // The API answers in English, so translated pages show the section's own message.
  var SERVER_TEXT_OK = /^en\b/i.test(document.documentElement.lang || "en");
  function serverMsg(payload, name, fallback) {
    return (SERVER_TEXT_OK && payload && payload.message) || msg(name, fallback);
  }
  var set = slot.getAttribute("data-set") || "cad";
  if (!handle) return;

  var accordion = slot.parentElement; // <div class="wrap-wide">
  var section = slot.closest("section");
  var signinUrl = "/pages/trade-signin";

  /* All dealer-source rows use the same generic-file placeholder the existing
     CAD metafield row renders — resource-icon 'spec'. Keeps the accordion
     visually uniform; a specifier scanning the list should see one shape per
     "here's a download" row, not three competing glyphs. */
  var specIconTemplate = document.querySelector('template[data-dealer-files-icon="spec"]');

  /* If Liquid already rendered metafield accordion rows we NEVER hide the
     section, regardless of what the sales app returns — a product with
     metafield-driven photos/renders/CAD is a valid "has resources" state
     on its own. Only products with an empty metafield state (Aarhus,
     Swish, height-adjustable-desk, etc. — most of the ~180 non-Omni
     catalog) are candidates for the hide-if-empty fallback below. */
  var hasMetafieldRows = section && section.getAttribute("data-resources-metafield-has-rows") === "true";

  loadSummary()
    .then(function (payload) {
      var assets = payload && payload.product ? (payload.product.assets || []) : [];
      var productId = payload && payload.product ? payload.product.id : null;

      var newRows = [];
      if (assets.length && productId) {
        var takenKeywords = existingKindKeywords(accordion);
        for (var i = 0; i < assets.length; i++) {
          var a = assets[i];
          var kw = kindKeyword(a.kind);
          if (kw && takenKeywords.indexOf(kw) !== -1) continue;
          newRows.push(a);
        }
      }

      if (newRows.length) {
        slot.innerHTML = renderRows(newRows, productName || (payload.product.name || ""), productId);
        slot.removeAttribute("hidden");
        return;
      }

      /* Nothing to inject. Hide the whole section only if Liquid also
         produced nothing — otherwise we would drop the metafield content
         the page legitimately has to show. */
      if (!hasMetafieldRows && section) section.remove();
    })
    .catch(function (err) {
      console.warn("[ProductResourcesDealer] summary unavailable:", err);
      if (!hasMetafieldRows && section) section.remove();
    });

  slot.addEventListener("click", function (event) {
    var link = event.target.closest ? event.target.closest("[data-dealer-file-get]") : null;
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    handleDownload(link).catch(function (err) {
      showNote(link, err.message || msg("start-failed", "The download could not be started. Please try again."));
    });
  });

  /* ── Rendering ─────────────────────────────────────────────────────── */

  function esc(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function iconHtml() {
    return specIconTemplate ? specIconTemplate.innerHTML : "";
  }

  function renderRows(assets, productName, productId) {
    var pid = String(productId);
    var html = "";
    for (var i = 0; i < assets.length; i++) {
      var a = assets[i];
      var indexAttr = typeof a.index === "number"
        ? ' data-dealer-file-index="' + esc(String(a.index)) + '"'
        : "";
      html +=
        '<details class="res-group">' +
        '<summary>' +
        '<h4>' + esc(a.label) + '</h4>' +
        '<span class="res-ico"></span>' +
        '</summary>' +
        '<div class="res-grid">' +
        '<a class="res-card doc"' +
        ' href="#"' +
        ' data-dealer-file-get="' + esc(pid) + '"' +
        ' data-dealer-file-kind="' + esc(a.kind) + '"' +
        ' data-dealer-file-label="' + esc(a.label) + '"' +
        ' data-dealer-file-format="' + esc(a.format) + '"' +
        indexAttr +
        ' aria-label="Download ' + esc(a.label + " — " + productName) + '">' +
        '<div class="rc-ph">' + iconHtml() + '</div>' +
        '<div class="rc-ft">' +
        '<span class="rc-name">' + esc(productName) + '</span>' +
        '<span class="rc-type">' + esc(a.format) + '</span>' +
        '</div>' +
        '</a>' +
        '</div>' +
        '</details>';
    }
    return html;
  }

  /* ── Dedup helpers ─────────────────────────────────────────────────── */

  /* Reads existing accordion row headings so we can skip kinds that already
     appear as a metafield-driven row. Cheap DOM walk, deliberate — the
     alternative is asking the server which is over-engineering for one
     comparison per PDP load. */
  function existingKindKeywords(root) {
    var headings = root.querySelectorAll("details.res-group summary h4");
    var kws = [];
    for (var i = 0; i < headings.length; i++) {
      var text = String(headings[i].textContent || "").trim().toLowerCase();
      if (!text) continue;
      if (text.indexOf("sketchup") !== -1 || text.indexOf("skp") !== -1) kws.push("sketchup");
      if (text.indexOf("revit") !== -1 || text.indexOf("rfa") !== -1 || text.indexOf("rvt") !== -1) kws.push("revit");
      if (text.indexOf("cad") !== -1 || text.indexOf("dwg") !== -1 || text.indexOf("dxf") !== -1) kws.push("cad");
    }
    return kws;
  }

  function kindKeyword(kind) {
    if (kind === "sketchup") return "sketchup";
    if (kind === "revit") return "revit";
    if (kind === "cad") return "cad";
    return null;
  }

  /* ── Download flow ─────────────────────────────────────────────────── */

  function handleDownload(link, requireSession) {
    var productId = link.getAttribute("data-dealer-file-get");
    var kind = link.getAttribute("data-dealer-file-kind");
    var assetIndex = link.getAttribute("data-dealer-file-index");
    var indexParam = assetIndex ? "&index=" + encodeURIComponent(assetIndex) : "";

    return builderToken().then(function (token) {
      /* Protected files need a real dealer session, never the lead unlock.
         Keep the selected row in the continuation; each attempt mints a new ticket. */
      if (!token) {
        if (requireSession) throw new Error(SIGN_IN_REQUIRED);
        var gate = document.querySelector("[data-rdl]");
        if (gate) {
          /* The gate's own script shows its first panel from open(); removing
             the wrapper's hidden attribute here left every panel hidden, so
             visitors saw an empty box. Ask the gate to open, naming the file. */
          var fileName = link.querySelector(".rc-name") ? link.querySelector(".rc-name").textContent.trim() : "";
          gate.dispatchEvent(new CustomEvent("rdl:open", { detail: {
            name: fileName,
            dealerOnly: true,
            resume: function () { return handleDownload(link, true); }
          } }));
        } else {
          window.location.href = signinUrl;
        }
        return;
      }

      var originalLabel = link.querySelector(".rc-name") ? link.querySelector(".rc-name").textContent : "";
      setBusy(link, true);
      clearFeedback(link);

      return fetch(
        "/apps/builder/api/dealer/catalogue/asset-file?productId=" +
          encodeURIComponent(productId) +
          "&kind=" + encodeURIComponent(kind) +
          indexParam +
          "&resolve=1",
        {
          credentials: "include",
          headers: { Accept: "application/json", Authorization: "Bearer " + token },
        }
      )
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (payload) {
              setBusy(link, false, originalLabel);
              console.error("[ProductResourcesDealer] resolve failed", res.status, payload);
              /* 409: the product's folder holds several files of this kind
                 ("Bloom chair - metal legs" / "- wooden legs") and the server
                 refuses to guess. The dealer picks — same rule, same inline
                 chooser, as the dealer CAD library. Anything else is said on
                 the page: a click that silently reverts to the card label
                 reads as a broken button (43 of 91 SketchUp rows were 409s
                 in the 2026-10-05 resolve audit). */
              if (res.status === 409 && payload.candidates && payload.candidates.length) {
                renderChooser(link, productId, kind, payload, assetIndex);
                return false;
              }
              throw new Error(serverMsg(payload, "fetch-failed", "That file could not be fetched right now. Please try again, or request it from your account manager."));
            });
          }
          return res.json().catch(function () { return {}; }).then(function (payload) {
            setBusy(link, false, originalLabel);
            if (!payload.ticket) throw new Error(msg("not-authorised", "The download could not be authorised. Please try again."));
            startDownload(productId, kind, payload.ticket, assetIndex, null);
            return true;
          });
        })
        .catch(function (err) {
          console.error("[ProductResourcesDealer] resolve error", err);
          setBusy(link, false, originalLabel);
          showNote(link, err.message || msg("connection-failed", "The download could not be started. Please check your connection and try again."));
          throw err;
        });
    });
  }

  function setBusy(link, isBusy, restoreLabel) {
    var name = link.querySelector(".rc-name");
    if (!name) return;
    if (isBusy) {
      if (!name.hasAttribute("data-orig")) name.setAttribute("data-orig", name.textContent || "");
      name.textContent = FINDING_LABEL;
    } else {
      var orig = restoreLabel || name.getAttribute("data-orig");
      if (orig) name.textContent = orig;
    }
  }

  /* The feedback box sits after the card grid inside the row's <details>, so
     a chooser or a note reads as belonging to that row. One at a time. */
  function feedbackHost(link) {
    return link.closest ? link.closest("details.res-group") : null;
  }

  function clearFeedback(link) {
    var host = feedbackHost(link);
    var old = host ? host.querySelector("[data-dealer-file-feedback]") : null;
    if (old) old.parentNode.removeChild(old);
  }

  function feedbackBox(link) {
    clearFeedback(link);
    var host = feedbackHost(link);
    if (!host) return null;
    var box = document.createElement("div");
    box.setAttribute("data-dealer-file-feedback", "");
    host.appendChild(box);
    return box;
  }

  function showNote(link, message) {
    var box = feedbackBox(link);
    if (!box) return;
    box.className = "res-note";
    box.setAttribute("role", "status");
    box.textContent = message;
  }

  function renderChooser(link, productId, kind, payload, assetIndex) {
    var box = feedbackBox(link);
    if (!box) return;
    box.className = "res-choose";
    var reason = String(serverMsg(payload, "several-match", "Several files match.")).replace(/\.?\s*$/, ".");
    var html = '<p class="res-choose-note">' + esc(reason) + " " + esc(msg("choose-one", "Choose one:")) + "</p>";
    for (var i = 0; i < payload.candidates.length; i++) {
      var c = payload.candidates[i];
      var mb = c.sizeBytes ? " (" + Math.round(c.sizeBytes / 1048576) + " MB)" : "";
      html +=
        '<button type="button" class="res-choose-one" data-dealer-file-pick="' + esc(c.id) + '">' +
        esc(c.name) + esc(mb) +
        "</button>";
    }
    box.innerHTML = html;
    box.addEventListener("click", function (ev) {
      var pick = ev.target.closest ? ev.target.closest("[data-dealer-file-pick]") : null;
      if (!pick) return;
      ev.preventDefault();
      clearFeedback(link);
      // The server re-checks that this id is inside the product's own folder.
      startDownload(productId, kind, payload.ticket, assetIndex, pick.getAttribute("data-dealer-file-pick"));
    });
  }

  function startDownload(productId, kind, ticket, assetIndex, fileId) {
    var url =
      "/apps/builder/api/dealer/catalogue/asset-file?productId=" +
      encodeURIComponent(productId) +
      "&kind=" + encodeURIComponent(kind) +
      (assetIndex ? "&index=" + encodeURIComponent(assetIndex) : "") +
      (fileId ? "&fileId=" + encodeURIComponent(fileId) : "") +
      (ticket ? "&ticket=" + encodeURIComponent(ticket) : "");
    var a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /* Mints the reseller-app's short-lived Builder Token from a cookie
     session on the shared parent domain. Copy of the helper in
     assets/section-dealer-cad-library.js — kept inline rather than
     imported because Shopify theme JS has no module resolver, and a
     20-line duplicate is cheaper than shipping a shared bundle for two
     consumers. Refactor when a third caller appears. */
  function builderToken() {
    var api = window.__dealerApi;
    if (!api) return Promise.resolve(null);
    return fetch(api + "/api/dealer/builder-token", { credentials: "include" })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) { return (data && data.token) || null; })
      .catch(function () { return null; });
  }

  function loadSummary() {
    var url =
      "/apps/builder/api/public/catalogue/asset-summary?productHandle=" +
      encodeURIComponent(handle) +
      "&set=" + encodeURIComponent(set);
    return fetch(url, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then(function (res) {
      if (res.status === 404) return { product: null };
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    });
  }
})();
